import { CircleAlert, LogIn, ShieldCheck, UserPlus } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useI18n } from '../../i18n/useI18n';
import { ApiError } from '../../lib/api-client';
import type { CurrentUser } from '../../lib/api-types';
import { fetchPublicConfig, login, loginWithGoogle, register } from './auth.api';

type AuthMode = 'login' | 'register';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(options: {
            callback: (response: { credential?: string }) => void;
            client_id: string;
          }): void;
          renderButton(
            element: HTMLElement,
            options: {
              size: 'large';
              text: 'signin_with' | 'signup_with';
              theme: 'outline';
              type: 'standard';
              width: number;
            },
          ): void;
        };
      };
    };
  }
}

let googleScript: Promise<void> | null = null;

export function AuthPage({
  onAuthenticated,
}: {
  readonly onAuthenticated: (user: CurrentUser) => void;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<AuthMode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [googleClientId, setGoogleClientId] = useState('');
  const [pending, setPending] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    void fetchPublicConfig()
      .then((config) => {
        if (active) setGoogleClientId(config.googleClientId);
      })
      .catch(() => {
        if (active) setGoogleClientId('');
      });
    return () => {
      active = false;
    };
  }, []);

  const authenticateGoogle = useCallback(
    async (credential: string) => {
      setError(null);
      setGooglePending(true);
      try {
        onAuthenticated(await loginWithGoogle(credential));
      } catch (caught) {
        setError(errorMessage(caught, t('auth.genericError')));
      } finally {
        setGooglePending(false);
      }
    },
    [onAuthenticated, t],
  );

  useEffect(() => {
    const target = googleButtonRef.current;
    if (!googleClientId || target === null) return;

    let active = true;
    target.replaceChildren();
    void loadGoogleScript()
      .then(() => {
        if (!active || window.google === undefined) return;
        window.google.accounts.id.initialize({
          callback: (response) => {
            if (response.credential) void authenticateGoogle(response.credential);
            else setError(t('auth.googleUnavailable'));
          },
          client_id: googleClientId,
        });
        window.google.accounts.id.renderButton(target, {
          size: 'large',
          text: mode === 'register' ? 'signup_with' : 'signin_with',
          theme: 'outline',
          type: 'standard',
          width: Math.min(360, target.clientWidth || 360),
        });
      })
      .catch(() => {
        if (active) setError(t('auth.googleUnavailable'));
      });

    return () => {
      active = false;
      target.replaceChildren();
    };
  }, [authenticateGoogle, googleClientId, mode, t]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const user =
        mode === 'login'
          ? await login({ email, password })
          : await register({ email, name, password });
      onAuthenticated(user);
    } catch (caught) {
      setError(errorMessage(caught, t('auth.genericError')));
    } finally {
      setPending(false);
    }
  };

  const toggleMode = () => {
    setError(null);
    setMode((current) => (current === 'login' ? 'register' : 'login'));
  };

  return (
    <main className="auth-page" id="main-content">
      <section className="auth-panel" aria-labelledby="auth-title">
        <div className="auth-brand">
          <ShieldCheck aria-hidden="true" />
          <span>{t('auth.brand')}</span>
        </div>
        <div className="auth-heading">
          <h1 id="auth-title">{t(mode === 'login' ? 'auth.titleLogin' : 'auth.titleRegister')}</h1>
          <span>{t(mode === 'login' ? 'auth.loginSubtitle' : 'auth.registerSubtitle')}</span>
        </div>
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          {mode === 'register' && (
            <label>
              {t('auth.name')}
              <input
                autoComplete="name"
                maxLength={100}
                name="name"
                onChange={(event) => setName(event.target.value)}
                required
                type="text"
                value={name}
              />
            </label>
          )}
          <label>
            {t('auth.email')}
            <input
              autoComplete="email"
              maxLength={254}
              name="email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
          <label>
            {t('auth.password')}
            <input
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              maxLength={128}
              minLength={mode === 'register' ? 12 : 1}
              name="password"
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
            {mode === 'register' && <small>{t('auth.passwordHint')}</small>}
          </label>
          {error !== null && (
            <p className="auth-error" role="alert">
              <CircleAlert aria-hidden="true" />
              {error}
            </p>
          )}
          <button className="button button-primary auth-submit" disabled={pending} type="submit">
            {mode === 'login' ? <LogIn aria-hidden="true" /> : <UserPlus aria-hidden="true" />}
            {pending
              ? t('common.saving')
              : t(mode === 'login' ? 'auth.submitLogin' : 'auth.submitRegister')}
          </button>
        </form>
        {googleClientId && (
          <div
            aria-busy={googlePending}
            aria-label={mode === 'login' ? t('auth.login') : t('auth.register')}
            className="auth-google"
            ref={googleButtonRef}
          />
        )}
        <button className="auth-mode-toggle" onClick={toggleMode} type="button">
          {t(mode === 'login' ? 'auth.showRegister' : 'auth.showLogin')}
        </button>
      </section>
    </main>
  );
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function loadGoogleScript(): Promise<void> {
  if (window.google !== undefined) return Promise.resolve();
  googleScript ??= new Promise((resolve, reject) => {
    const current = document.querySelector<HTMLScriptElement>('script[data-financego-google]');
    if (current !== null) {
      current.addEventListener('load', () => resolve(), { once: true });
      current.addEventListener('error', () => reject(new Error('Google script failed')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.dataset.financegoGoogle = 'true';
    script.defer = true;
    script.src = 'https://accounts.google.com/gsi/client';
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Google script failed')), {
      once: true,
    });
    document.head.append(script);
  });
  return googleScript;
}
